/**
 * Resolve the model config a Tangle agent's sandbox/runtime runs on.
 *
 * Every Tangle agent product resolves the SAME thing from env: the Tangle Router
 * (OpenAI-compatible, metered at the platform markup against a single
 * `TANGLE_API_KEY`) by default, with a direct-Anthropic BYOK escape hatch. The
 * shape feeds the sandbox SDK's `backend.model`. Lifted here so no product
 * hand-rolls the env parsing + the router default.
 */

export interface TangleModelConfig {
  /** The Tangle Router is OpenAI-compatible → driven via `openai-compat`.
   *  `anthropic` is the BYOK escape hatch. */
  provider: 'openai-compat' | 'anthropic'
  model: string
  apiKey: string
  baseUrl: string
}

/** Define the environment context for executing Tangle operations */
export type TangleExecutionEnvironment = 'development' | 'staging' | 'production' | 'test'
/** Resolve the source of the Tangle execution key as either local environment or user input */
export type TangleExecutionKeySource = 'local-env' | 'user'
/** Define error codes for Tangle execution key issues related to API key and account connection */
export type TangleExecutionKeyErrorCode =
  | 'local_tangle_api_key_required'
  | 'tangle_account_not_connected'

/** Resolve options for model configuration including environment variables and default router base URL */
export interface ResolveModelOptions {
  /** Env to read (defaults to process.env). */
  env?: Record<string, string | undefined>
  /** Router base URL default when `TANGLE_ROUTER_BASE_URL` is unset. */
  defaultRouterBaseUrl?: string
}

/** Resolve options for retrieving user API keys within a specific Tangle execution environment */
export interface ResolveUserTangleExecutionKeyOptions {
  /** Deployment context. Only local development may fall back to env keys. */
  environment?: TangleExecutionEnvironment
  /** Env to read for the local-development fallback. */
  env?: Record<string, string | undefined>
  /** App-owned lookup for the caller's linked platform API key. */
  getUserApiKey: () => string | null | undefined | Promise<string | null | undefined>
}

/** Resolve options for retrieving a user's Tangle execution key with environment and API key access parameters */
export interface ResolveUserTangleExecutionKeyForUserOptions<UserId = string> {
  userId: UserId
  environment?: TangleExecutionEnvironment
  env?: Record<string, string | undefined>
  getUserApiKey: (userId: UserId) => string | null | undefined | Promise<string | null | undefined>
}

/** Define a resolved key combining an API key with its Tangle execution source */
export interface ResolvedTangleExecutionKey {
  apiKey: string
  source: TangleExecutionKeySource
}

/** Resolve options for retrieving a Tangle developer or user API key based on environment and context */
export interface ResolveTangleDevOrUserKeyOptions {
  /** Deployment context. Only local development may use the env key. */
  environment?: TangleExecutionEnvironment
  /** Env to read for the local-development fallback. */
  env?: Record<string, string | undefined>
  /** App-owned lookup for the caller's linked platform API key. */
  getUserApiKey: () => string | null | undefined | Promise<string | null | undefined>
}

/** Represent HTTP error response containing status, error message, and specific error code */
export interface TangleExecutionKeyHttpError {
  status: number
  body: {
    error: string
    code: TangleExecutionKeyErrorCode
  }
}

/** Define configuration options for creating a Tangle router model including API key and model details */
export interface CreateTangleRouterModelConfigOptions {
  apiKey: string
  model: string
  baseUrl?: string
}

/** Define options for configuring billing enforcement environment variables and overrides */
export interface TangleBillingEnforcementOptions {
  /** Env to read (defaults to process.env). */
  env?: Record<string, string | undefined>
  /**
   * Optional app-specific override flag, e.g. `GTM_BILLING_ENFORCEMENT`.
   * Defaults to the shared `TANGLE_BILLING_ENFORCEMENT`.
   */
  enforcementEnvVar?: string
}

/** Provide the default base URL for the Tangle router API endpoint */
export const DEFAULT_TANGLE_ROUTER_BASE_URL = 'https://router.tangle.tools/v1'
/** Define the default environment variable name for Tangle billing enforcement */
export const DEFAULT_TANGLE_BILLING_ENFORCEMENT_ENV_VAR = 'TANGLE_BILLING_ENFORCEMENT'

function requireEnv(env: Record<string, string | undefined>, name: string): string {
  const value = env[name]?.trim()
  if (!value) throw new Error(`${name} is required`)
  return value
}

/** Resolve a string by trimming whitespace or returning null if empty or undefined */
export function trimOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

function isTangleExecutionKeyErrorCode(value: unknown): value is TangleExecutionKeyErrorCode {
  return value === 'local_tangle_api_key_required' || value === 'tangle_account_not_connected'
}

/** Represent execution key errors with specific codes and HTTP status information */
export class TangleExecutionKeyError extends Error {
  readonly code: TangleExecutionKeyErrorCode
  readonly status: number

  constructor(code: TangleExecutionKeyErrorCode, message: string, status: number) {
    super(message)
    this.name = 'TangleExecutionKeyError'
    this.code = code
    this.status = status
  }
}

/** Identify whether an error is a TangleExecutionKeyError based on its properties and type */
export function isTangleExecutionKeyError(error: unknown): error is TangleExecutionKeyError {
  return error instanceof TangleExecutionKeyError
    || (
      typeof error === 'object'
      && error !== null
      && (error as { name?: unknown }).name === 'TangleExecutionKeyError'
      && typeof (error as { message?: unknown }).message === 'string'
      && isTangleExecutionKeyErrorCode((error as { code?: unknown }).code)
      && typeof (error as { status?: unknown }).status === 'number'
    )
}

/** Resolve the current Tangle execution environment based on provided or process environment variables */
export function resolveTangleExecutionEnvironment(
  env: Record<string, string | undefined> = process.env as Record<string, string | undefined>,
): TangleExecutionEnvironment {
  const raw = (env.APP_ENV ?? env.NODE_ENV ?? '').trim().toLowerCase()
  if (raw === 'development' || raw === 'dev' || raw === 'local') return 'development'
  if (raw === 'staging') return 'staging'
  if (raw === 'test') return 'test'
  return 'production'
}

/**
 * Shared policy for agent products that bill through the Tangle Platform.
 *
 * Local development defaults billing enforcement off so apps can use a local
 * `TANGLE_API_KEY` without requiring a browser-linked platform account. Any
 * non-development environment defaults enforcement on. Apps may pass their own
 * override flag (`FOO_BILLING_ENFORCEMENT`) while new apps can use the shared
 * `TANGLE_BILLING_ENFORCEMENT`.
 */
export function isTangleBillingEnforcementDisabled(
  opts: TangleBillingEnforcementOptions = {},
): boolean {
  const env = opts.env ?? (process.env as Record<string, string | undefined>)
  const enforcementEnvVar = opts.enforcementEnvVar ?? DEFAULT_TANGLE_BILLING_ENFORCEMENT_ENV_VAR
  const override = env[enforcementEnvVar]?.trim().toLowerCase()

  if (override === 'disabled') return true
  if (override === 'enabled') return false

  return resolveTangleExecutionEnvironment(env) === 'development'
}

/** Resolve and format TangleExecutionKey HTTP errors into a standardized error object or return null */
export function tangleExecutionKeyHttpError(error: unknown): TangleExecutionKeyHttpError | null {
  if (!isTangleExecutionKeyError(error)) return null
  return {
    status: error.status,
    body: {
      error: error.message,
      code: error.code,
    },
  }
}

/**
 * Shared dev-aware Tangle key resolution. Local development may use a server
 * `TANGLE_API_KEY`; otherwise the caller's linked platform key is used. Returns
 * null when neither resolves, so each caller can throw its own domain error
 * (e.g. model-execution 503/401 vs hub `tangle_link_required` 412).
 */
export async function resolveTangleDevOrUserKey(
  opts: ResolveTangleDevOrUserKeyOptions,
): Promise<ResolvedTangleExecutionKey | null> {
  const env = opts.env ?? (process.env as Record<string, string | undefined>)
  const environment = opts.environment ?? resolveTangleExecutionEnvironment(env)

  if (environment === 'development') {
    const apiKey = trimOrNull(env.TANGLE_API_KEY)
    if (apiKey) return { apiKey, source: 'local-env' }
  }

  const apiKey = trimOrNull(await opts.getUserApiKey())
  if (apiKey) return { apiKey, source: 'user' }

  return null
}

/**
 * Resolve the user-facing Tangle API key for model execution.
 *
 * Local development may use a server env key so apps remain easy to run.
 * Deployed contexts must use the caller's linked platform key; this keeps
 * model execution, billing, and account ownership aligned across products.
 */
export async function resolveUserTangleExecutionKey(
  opts: ResolveUserTangleExecutionKeyOptions,
): Promise<ResolvedTangleExecutionKey> {
  const env = opts.env ?? (process.env as Record<string, string | undefined>)
  const environment = opts.environment ?? resolveTangleExecutionEnvironment(env)

  const resolved = await resolveTangleDevOrUserKey({ environment, env, getUserApiKey: opts.getUserApiKey })
  if (resolved) return resolved

  if (environment === 'development') {
    throw new TangleExecutionKeyError(
      'local_tangle_api_key_required',
      'TANGLE_API_KEY or a linked Tangle account is required for local Tangle model execution.',
      503,
    )
  }

  throw new TangleExecutionKeyError(
    'tangle_account_not_connected',
    'Connect your Tangle account before invoking this agent.',
    401,
  )
}

/** Resolve the Tangle execution key for a specified user using provided environment and API key options */
export async function resolveUserTangleExecutionKeyForUser<UserId = string>(
  opts: ResolveUserTangleExecutionKeyForUserOptions<UserId>,
): Promise<ResolvedTangleExecutionKey> {
  return resolveUserTangleExecutionKey({
    environment: opts.environment,
    env: opts.env,
    getUserApiKey: () => opts.getUserApiKey(opts.userId),
  })
}

/**
 * Build an OpenAI-compatible Tangle Router model config from an already
 * resolved execution key. This intentionally does not read TANGLE_API_KEY.
 */
export function createTangleRouterModelConfig(
  opts: CreateTangleRouterModelConfigOptions,
): TangleModelConfig {
  const apiKey = opts.apiKey.trim()
  if (!apiKey) throw new Error('apiKey is required')
  const model = opts.model.trim()
  if (!model) throw new Error('model is required')
  return {
    provider: 'openai-compat',
    model,
    apiKey,
    baseUrl: (opts.baseUrl?.trim() || DEFAULT_TANGLE_ROUTER_BASE_URL).replace(/\/+$/, ''),
  }
}

/**
 * Resolve the model config from env. DEFAULT path (`MODEL_PROVIDER` unset or
 * `openai-compat`/`tangle-router`/`tcloud`): the Tangle Router, authenticated
 * with `TANGLE_API_KEY`, model from `MODEL_NAME`. BYOK path
 * (`MODEL_PROVIDER=anthropic`): direct Anthropic with `ANTHROPIC_API_KEY` +
 * `ANTHROPIC_BASE_URL`. Throws (fail-loud) on a missing required var so a
 * misconfigured deploy fails at boot, not mid-turn.
 */
export function resolveTangleModelConfig(opts: ResolveModelOptions = {}): TangleModelConfig {
  const env = opts.env ?? (process.env as Record<string, string | undefined>)
  const provider = env.MODEL_PROVIDER?.trim() || 'openai-compat'
  const model = requireEnv(env, 'MODEL_NAME')

  if (provider === 'openai-compat' || provider === 'tangle-router' || provider === 'tcloud') {
    return {
      provider: 'openai-compat',
      model,
      apiKey: requireEnv(env, 'TANGLE_API_KEY'),
      baseUrl: (env.TANGLE_ROUTER_BASE_URL?.trim() || opts.defaultRouterBaseUrl || DEFAULT_TANGLE_ROUTER_BASE_URL).replace(/\/+$/, ''),
    }
  }

  if (provider === 'anthropic') {
    return {
      provider,
      model,
      apiKey: requireEnv(env, 'ANTHROPIC_API_KEY'),
      baseUrl: requireEnv(env, 'ANTHROPIC_BASE_URL'),
    }
  }

  throw new Error(`Unsupported MODEL_PROVIDER: ${provider} (use openai-compat for the Tangle Router, or anthropic for BYOK)`)
}
